
import { Component, Vue } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import OpenInvoices from "@/views/Accounting/OpenInvoices.vue";
import { PaymentTransactionDto } from "@/dtos/paymentTransactionDto";
import { MatchedInvoiceDto } from "@/dtos/matchedInvoiceDto";
import { MatchingType } from "@/dtos/matchingType";
import { InvoiceManagementDto } from "@/dtos/invoiceManagementDto";
import { PaymentDto } from "@/dtos/paymentDto";
import CreditOverdrawnOrdersDialog from "./CreditOverdrawnOrdersDialog.vue";
import DatePicker from "@/components/DatePicker.vue";
import moment from "moment";

@Component({
	components: {
		OpenInvoices,
		DatePicker,
		CreditOverdrawnOrdersDialog,
	},
})
export default class OpenInvoiceMatchingComponent extends Vue {
	private reloadInvoicesToggle: boolean = false;
	private uploading: boolean = false;
	private processing: boolean = false;
	private paidInvoiceIds: number[] = [];
	private fromDateString: string = moment().subtract(12, "month").format("YYYY-MM-DD");
	private toDateString: string = moment().format("YYYY-MM-DD");

	private get transactions(): PaymentTransactionDto[] {
		return this.$store.state.transactions.sort(
			(a: PaymentTransactionDto, b: PaymentTransactionDto) => {
				let invoicesA = this.getMatchingInvoicesOfTransaction(a.id);
				let invoicesB = this.getMatchingInvoicesOfTransaction(b.id);
				let bestMatchA = Math.min.apply(
					null,
					invoicesA.map((x: any) => x.matchingType)
				);
				let bestMatchB = Math.min.apply(
					null,
					invoicesB.map((x: any) => x.matchingType)
				);
				let compare = bestMatchA - bestMatchB;
				if (compare === 0) {
					compare = a.id - b.id;
				}
				return compare;
			}
		);
	}
	private set transactions(transactions: PaymentTransactionDto[]) {
		this.$store.commit("setTransactions", transactions);
	}

	private get matchedInvoices() {
		return this.$store.state.matchedInvoices;
	}
	private set matchedInvoices(matchedInvoices: MatchedInvoiceDto[]) {
		this.$store.commit("setMatchedInvoices", matchedInvoices);
	}

	private matchInvoiceToSelection(invoice: InvoiceManagementDto) {
		if (this.transactions.some((t: PaymentTransactionDto) => t.isMatchSelectMode)) {
			let selectedTransaction = this.transactions.filter(
				(t: PaymentTransactionDto) => t.isMatchSelectMode
			)[0];
			console.log(
				`matching transaction ${selectedTransaction.id} to invoice ${invoice.invoiceId}`
			);
			this.$store.commit("addTransactionMatch", {
				transactionId: selectedTransaction.id,
				invoice: invoice,
			});
			this.toggleMatchModeForTransaction(selectedTransaction.id, false);
		}
	}

	private getMatchingInvoicesOfTransaction(transactionId: number): MatchedInvoiceDto[] {
		let matchingInvoices = this.matchedInvoices.filter(
			(mi: MatchedInvoiceDto) => mi.transactionId === transactionId
		);
		if (matchingInvoices.length > 0) {
			return matchingInvoices;
		} else {
			let emptyArray: MatchedInvoiceDto[] = [];
			let emptyMatch: MatchedInvoiceDto = {
				transactionId: transactionId,
				invoiceId: 0,
				matchingType: MatchingType.NoMatch,
				customerId: 0,
				amount: 0,
				name: "",
				kundenauftragId: 0,
				dueDate: new Date(),
				payments: new Array<PaymentDto>(),
			};
			emptyArray.push(emptyMatch);
			return emptyArray;
		}
	}

	dragover(event: any) {
		event.preventDefault();
		(this.$refs.uploadArea as any).className = "dragArea dragging";
	}
	dragleave(event: any) {
		(this.$refs.uploadArea as any).className = "dragArea";
	}
	drop(event: any) {
		event.preventDefault();
		this.uploadFiles(null, event.dataTransfer.files);
		(this.$refs.uploadArea as any).className = "dragArea";
	}

	private reloadInvoices() {
		this.reloadInvoicesToggle = true;
	}

	private reloadMahnungen() {
		this.$emit("reloadMahnungen");
	}

	private deleteAllTransactions() {
		this.transactions = [];
		this.matchedInvoices = [];
	}

	private deleteTransaction(id: number) {
		this.$store.commit("removeTransaction", id);
	}

	private disconnectTransaction(invoiceId: number) {
		this.$store.commit("removeTransactionMatch", invoiceId);
	}

	private toggleMatchModeForTransaction(transactionId: number, selectMode: boolean) {
		this.$store.commit("setMatchSelectMode", {
			transactionId: transactionId,
			isSelectMode: selectMode,
		});
		this.$forceUpdate();
	}

	private async uploadFiles(event: any, inputFiles: FileList) {
		const allowedExtensions = ["zip", "xml"];
		const files = Array.from(inputFiles).filter((file: File) => {
			const extension = file.name.slice(file.name.lastIndexOf(".") + 1);
			console.log("extension", extension);
			return allowedExtensions.includes(extension.toLowerCase());
		});
		if (files.length === 0) {
			console.log("No files selected.");
			return;
		}
		if (files.length > 10) {
			this.$store.commit(
				"setSnackbarText",
				"Sie können maximal 10 CAD Modelle gleichzeitig hochladen."
			);
			return;
		}
		console.log("Uploading files...");
		try {
			this.transactions = [];
			this.uploading = true;
			let data = new FormData();
			let index = 0;

			data.append("from", this.fromDateString);
			data.append("to", this.toDateString);
			for (const file of files) {
				data.append("files", file);
				console.log(`Uploading File ${index}: ${file.name}`);
				index++;
			}

			await httpClient()
				.post("Invoice/ExtractTransactionsFromFiles", data, {
					headers: {
						"Content-Type": "multipart/form-data",
					},
				})
				.then((response: any) => {
					let transactions = response.data.transactions;
					let matchedInvoices = response.data.invoices;
					transactions.forEach((t: any) => {
						t.isMatchSelectMode = false;
					});
					this.transactions = transactions;
					this.matchedInvoices = matchedInvoices;
				});

			if (event != null) {
				event.target.value = "";
			}
		} catch (err) {
			console.log(`Fehler beim Upload. ${err}`);
			this.$store.commit("setSnackbarErrorText", "Upload fehlgeschlagen.");
		} finally {
			this.uploading = false;
		}
	}

	private async processMatches(transactionIds: number[]) {
		try {
			this.processing = true;
			let transactionsToSend = this.transactions.filter((t: PaymentTransactionDto) =>
				transactionIds.includes(t.id)
			);
			let invoicesToSend = this.matchedInvoices.filter((i: MatchedInvoiceDto) =>
				transactionIds.includes(i.transactionId)
			);
			let sendObject = {
				transactions: transactionsToSend,
				invoices: invoicesToSend,
			};

			await httpClient().post("Invoice/ProcessMatchedTransactions", sendObject);
			transactionIds.forEach((id) => this.$store.commit("removeTransaction", id));
			// This removes the open invoice from the list.
			// If the transaction does not fully cover the invoice, this is incorrect.
			// But after a reload the open transaction is in the list again.
			// I think this is the better solution than to not remove the connected invoice from the list
			// because most likely, in one batch of transactions, there are not more than one transactions for the same invoice.
			invoicesToSend.forEach((invoice) =>
				this.$store.commit("removeDueInvoice", invoice.invoiceId)
			);

			this.paidInvoiceIds = invoicesToSend.map((i) => i.invoiceId);
		} catch (err) {
			console.log(`Fehler bei der Verarbeiten. ${err}`);
			this.$store.commit("setSnackbarErrorText", "Verarbeitung fehlgeschlagen.");
		} finally {
			this.processing = false;
		}
	}

	private invoiceMultiMatch(invoiceId: number) {
		return (
			this.matchedInvoices.filter((i: MatchedInvoiceDto) => i.invoiceId == invoiceId).length >
			1
		);
	}

	private get anyTransactionsInSelectionMode() {
		return (
			this.transactions.filter((t: PaymentTransactionDto) => t.isMatchSelectMode).length > 0
		);
	}
	private isToggleDisabled(id: number) {
		return (
			this.anyTransactionsInSelectionMode &&
			!this.transactions[this.transactions.findIndex((i: any) => i.id === id)]
				.isMatchSelectMode
		);
	}

	private get isReadyToProcessMatches() {
		return (
			this.matchedInvoices.filter(
				(m: MatchedInvoiceDto) =>
					m.matchingType != MatchingType.ByPaidInvoice &&
					m.matchingType != MatchingType.NoMatch
			).length > 0 && !this.processing
		);
	}

	private isReadyToProcessTransaction(transactionId: number) {
		return (
			this.matchedInvoices.some(
				(mi: MatchedInvoiceDto) =>
					mi.transactionId === transactionId &&
					mi.matchingType != MatchingType.ByPaidInvoice
			) && !this.processing
		);
	}

	private navigateToPayments(invoideId: number) {
		this.$emit("navigateToPayments", invoideId);
	}

	private getBackgroundColor(isSelectMode: boolean) {
		return isSelectMode ? "#efbb20" : "white";
	}

	private getMatchIcon(type: MatchingType) {
		switch (type) {
			case MatchingType.NoMatch:
				return "cancel";
			case MatchingType.ById:
			case MatchingType.ByReference:
			case MatchingType.ByAmount:
				return "check_circle";
			case MatchingType.ByHand:
				return "pan_tool";
			case MatchingType.ByPaidInvoice:
				return "warning";
		}
	}
	private getMatchIconColor(type: MatchingType) {
		switch (type) {
			case MatchingType.NoMatch:
				return "red";
			case MatchingType.ById:
			case MatchingType.ByHand:
				return "green";
			case MatchingType.ByReference:
				return "lime darken-2";
			case MatchingType.ByAmount:
				return "orange";
			case MatchingType.ByPaidInvoice:
				return "purple";
		}
	}
	private getMatchText(type: MatchingType) {
		switch (type) {
			case MatchingType.NoMatch:
				return "";
			case MatchingType.ById:
				return "Via ID";
			case MatchingType.ByHand:
				return "Via manueller Zuordnung";
			case MatchingType.ByReference:
				return "Via Referenz-Text";
			case MatchingType.ByAmount:
				return "Via Betrag";
			case MatchingType.ByPaidInvoice:
				return "Bereits bezahlt oder storniert";
		}
	}

	private setFromDate(date: Date) {
		this.fromDateString = moment(date).format("YYYY-MM-DD");
	}
	private setToDate(date: Date) {
		this.toDateString = moment(date).format("YYYY-MM-DD");
	}
}
